import { useSearchParams } from "react-router-dom";
import type { SortKey } from "../components/investments/DepotTable";

export type SortOrder = "asc" | "desc";

/** Default-Sort fuer die DepotTable — Marktwert absteigend. */
export const DEFAULT_SORT_KEY: SortKey = "value";
export const DEFAULT_SORT_ORDER: SortOrder = "desc";

/**
 * Whitelist gueltiger `sort`-Werte aus der URL: alle Spalten-Keys (Single- +
 * Multi-Zeilen) plus Sub-Sort-Keys fuer die 2./3. Mini-Zeile der Multi-Spalten.
 */
const ALL_SORT_KEYS: ReadonlySet<string> = new Set<SortKey>([
  "name",
  "quantity",
  "price",
  "value",
  "purchaseValue",
  "gainLoss",
  "dividends",
  "depotShare",
  "priceDiffPct",
  "valueDiffAbs",
  "valueDiffPct",
]);

/**
 * URL-State-Hook fuer die /investments-Page.
 *
 * Konvention "clean URLs": Default-Werte werden NICHT als Query-Param geschrieben,
 * sondern aus der URL geloescht. Sort-State: `sort` (Spalten-Key) + `order`
 * (`asc`|`desc`). Default ist `value desc` (Marktwert absteigend).
 *
 * Frueher hier verwaltete `tab`/`cols`-Params sind weggefallen: kein Tab-Switch
 * mehr (Performance-Lifetime ist eine KPI-Kachel) und keine Spalten-Toggle (alle
 * Spalten sind immer sichtbar).
 */
export function useInvestmentsUrlState() {
  const [params, setParams] = useSearchParams();

  const sortParam = params.get("sort");
  const sort: SortKey =
    sortParam !== null && ALL_SORT_KEYS.has(sortParam) ? (sortParam as SortKey) : DEFAULT_SORT_KEY;

  const orderParam = params.get("order");
  const order: SortOrder = orderParam === "asc" ? "asc" : "desc";

  return {
    sort,
    order,
    setSort: (nextSort: SortKey, nextOrder: SortOrder) => {
      const p = new URLSearchParams(params);
      if (nextSort === DEFAULT_SORT_KEY && nextOrder === DEFAULT_SORT_ORDER) {
        p.delete("sort");
        p.delete("order");
      } else {
        p.set("sort", nextSort);
        p.set("order", nextOrder);
      }
      setParams(p, { replace: true });
    },
  };
}
